import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { User } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { LocalStorageKey } from '../../data/client_local_storage.ts';
import { useHousehold } from '../../data/use_household.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert } from '../../util/confirm.tsx';
import { Button } from './button.tsx';
import { goToNextOnboardingStep, Message, TextInput } from './common.tsx';
import { Frame } from './frame.tsx';

// Collects the first name of the person creating a brand new household (additional members can be added later).
// Households joined via HouseholdFind/HouseholdJoin instead choose from the existing member list (ChooseUser).
export const HouseholdFirstName = () => {
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ref rather than state so a second tap during the same render cycle is also blocked
  const isSubmittingRef = useRef(false);
  const { household, setHousehold } = useHousehold();
  const setActiveUserToken = useLocalStorage(LocalStorageKey.ActiveUserToken)[1];
  const navigate = useNavigate();

  useEffect(() => {
    firstNameInputRef.current?.focus();
  }, [firstNameInputRef]);

  const onFirstNameChange = useCallback(() => {
    setFirstName(firstNameInputRef.current?.value ?? '');
  }, []);

  const onContinue = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }
    const name = firstName.trim();
    if (!name) {
      await alert({ confirmation: <span>Please enter your first name</span> });
      return;
    }
    if (!household) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      let user: User;
      try {
        const { token } = await server_api.upsertUser({ name });
        user = { token, name };
      } catch {
        void alert({ confirmation: <span>Network error - please try again</span> });
        return;
      }
      try {
        await setHousehold({ ...household, users: [...(household.users ?? []), user] });
      } catch {
        void alert({ confirmation: <span>Network error - please try again</span> });
        return;
      }
      setActiveUserToken(user.token);
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdFirstName, action: 'continue' });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [firstName, household, navigate, setActiveUserToken, setHousehold]);

  return (
    <Frame
      onboardingPage={OnboardingPages.HouseholdFirstName}
      mainContent={
        <>
          <Prompt $widthInChars={20} $lessPadding={true}>
            What&apos;s your first name?
          </Prompt>
          <Subtitle>You can add family members later.</Subtitle>
          <TextInput
            type="text"
            enterKeyHint="next"
            onChange={onFirstNameChange}
            ref={firstNameInputRef}
            autoCapitalize="words"
          />
        </>
      }
      footerContent={
        <Button type="submit" onClick={onContinue} disabled={!firstName.trim()} busy={isSubmitting}>
          CONTINUE
        </Button>
      }
      onEnterKeypress={onContinue}
      containsInputElement={true}
    />
  );
};

const Prompt = styled(Message)`
  width: auto;
  text-align: center;
`;

const Subtitle = styled.div`
  font-size: 0.9em;
  color: var(--color-text-light);
  padding-bottom: 2em;
`;
